<!-- app-subdomain-creators/src/Components/NewsfeedCreatorSearchTab.vue -->

<template>
  <div class="h-full pt-4 rounded w-full pb-10">
    <div class="rounded lg:w-[1175px] mx-auto flex gap-4 min-h-[650px]">
      <!-- LEFT: Search results -->
      <div class="w-[60%]">
        <!-- Header -->
        <div class="mb-3">
          <h2 class="text-lg font-semibold text-zinc-900">
            Search results
          </h2>
          <p class="text-sm text-zinc-500 mt-1">
            <span v-if="currentTerm">
              Showing matches for
              <span class="font-semibold text-zinc-900">“{{ currentTerm }}”</span>
              in this group.
            </span>
            <span v-else>
              Run a search from the search bar above to see results here.
            </span>
          </p>
        </div>

        <!-- Loading -->
        <div
          v-if="loading"
          class="text-sm text-zinc-400"
        >
          Searching…
        </div>

        <!-- Error -->
        <div
          v-if="!loading && errorMessage"
          class="text-sm text-red-500"
        >
          {{ errorMessage }}
        </div>

        <!-- Empty -->
        <div
          v-if="!loading && !errorMessage && currentTerm && !posts.length"
          class="text-sm text-zinc-400 mt-2"
        >
          No results for “{{ currentTerm }}”.
        </div>

        <!-- 🔹 Full post cards list -->
        <div
          v-if="!loading && !errorMessage && posts.length"
          class="mt-2 space-y-2"
        >
          <div class="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
            {{ totalLabel }}
          </div>

          <NewsfeedCreatorPost
            v-for="post in posts"
            :key="post.id"
            :post="post"
            :posting-as-name="postingAsName"
            :posting-as-avatar-url="postingAsAvatarUrl"
            context="search"
            :feature-instance-id="featureInstanceId"
            @edit-post="handleEditPostFromSearch"
            @save-post="handleSavePostFromSearch"
            @unsave-post="handleUnsavePostFromSearch"
            @pin-changed="handlePinChanged"
          />

          <button
            v-if="hasMore"
            type="button"
            class="mt-2 text-[12px] text-[#2D8CFF] hover:underline"
            @click="loadMore"
          >
            Load more results
          </button>
        </div>
      </div>

      <!-- RIGHT: helper box -->
      <div class="w-[40%]">
        <div
          class="w-full rounded-xl border-[1px] p-3 border-zinc-200 shadow-sm bg-white"
        >
          <h4 class="text-zinc-800 text-base font-medium pb-2">
            Tip
          </h4>
          <p class="text-zinc-500 text-sm">
            Use specific keywords to find posts or comments in this group.
            You can search by topic, phrase, or member name. Click a result
            again (or “Load more”) to refine what you see here.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  defineProps,
  ref,
  watch,
  computed,
  defineExpose,
} from 'vue';

import NewsfeedCreatorPost from '@/Components/NewsfeedCreatorPost.vue';

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const props = defineProps({
  featureInstanceId: {
    type: [Number, String],
    default: null,
  },
  postingAsName: {
    type: String,
    default: 'You',
  },
  postingAsAvatarUrl: {
    type: String,
    default: null,
  },
  // term passed from NewsfeedPageCreator when user clicked a result
  searchTerm: {
    type: String,
    default: '',
  },
});
const emit = defineEmits(['open-edit-post']);

const loading = ref(false);
const errorMessage = ref('');
const posts = ref([]); // normalized posts ready for <NewsfeedCreatorPost>
const limit = ref(10);
const offset = ref(0);
const hasMore = ref(false);
const total = ref(0);
const currentTerm = ref('');

// default avatar for author / poster
const defaultAvatar = new URL(
  '@/assets/media/images/default-avatar.png',
  import.meta.url
).href;

const totalLabel = computed(() => {
  if (!total.value) return '0 results';
  if (total.value === 1) return '1 result';
  return `${total.value} results`;
});

const handleEditPostFromSearch = ({ postId }) => {
  if (!postId) return;
  const postToEdit = posts.value.find((p) => String(p.id) === String(postId));
  if (!postToEdit) return;
  emit('open-edit-post', { post: { ...postToEdit } });
};

const buildCoverImageUrl = (fileId) => {
  if (!fileId) return null;
  return `${apiProxyUrl}/api/image/${fileId}`;
};

/**
 * Normalize a "post-like" row (same shape Discussion uses) so that
 * <NewsfeedCreatorPost> gets everything it expects:
 * - authorName / authorAvatar
 * - body / createdAt / timeAgo
 * - attachments (preserved, just adding fileId if needed)
 * - coverMediaFileId / imageUrl
 * - reactionsSummary / reactionNamesByType
 * - isSaved / isPinned
 */
function normalizePostRow(row) {
  if (!row || !row.id) return null;

  const currentUserName = props.postingAsName || 'You';
  const currentUserAvatar = props.postingAsAvatarUrl || defaultAvatar;

  // 🔹 Normalize isPinned from backend
  const isPinned = !!(row.isPinned ?? row.is_pinned ?? row.pinned);

  // 🔹 Normalize attachments but keep existing fields (type, urls, etc.)
  let attachments = [];
  if (Array.isArray(row.attachments)) {
    attachments = row.attachments
      .map((att) => {
        if (!att) return null;
        const normalized = { ...att };

        if (!normalized.fileId) {
          if (att.file_id) normalized.fileId = att.file_id;
          else if (att.mediaFileId) normalized.fileId = att.mediaFileId;
        }
        return normalized;
      })
      .filter(Boolean);
  }

  // 🔹 Derive coverMediaFileId – prefer explicit, else first attachment
  const coverMediaFileId =
    row.coverMediaFileId ||
    row.cover_media_file_id ||
    (attachments.length ? attachments[0].fileId : null);

  const imageUrl = coverMediaFileId
    ? buildCoverImageUrl(coverMediaFileId)
    : row.imageUrl || row.coverImageUrl || null;

  // ✅ reactions summary, if backend sends it
  const reactionsSummary =
    row.reactionsSummary ||
    row.reactions || {
      total: Number(row.reactionsCount || 0) || 0,
      like: 0,
      love: 0,
      care: 0,
      haha: 0,
      wow: 0,
      sad: 0,
      angry: 0,
    };

  // ✅ user lists by type (for tooltips)
  const reactionUsersByType =
    row.reactionUsersByType || row.reaction_users_by_type || {
      like: [],
      love: [],
      care: [],
      haha: [],
      wow: [],
      sad: [],
      angry: [],
    };

  const reactionNamesByType = {};
  const types = ['like', 'love', 'care', 'haha', 'wow', 'sad', 'angry'];
  for (const key of types) {
    const arr = Array.isArray(reactionUsersByType[key])
      ? reactionUsersByType[key]
      : [];

    reactionNamesByType[key] = arr
      .map((u) => {
        if (!u) return '';
        if (typeof u === 'string') return u.trim();
        if (typeof u.name === 'string') return u.name.trim();
        return '';
      })
      .filter(Boolean);
  }
  const reactionNamesLegacy = Object.values(reactionNamesByType).flat();

  const isSavedFromBackend =
    row.isSaved ?? row.is_saved ?? row.saved ?? false;

  return {
    id: row.id,

    // 🔹 "posted as" (creator identity for this post)
    authorName:
      row.postedAsName ||
      row.posted_as_name ||
      row.authorName ||
      row.author_name ||
      currentUserName,
    authorAvatar:
      row.postedAsAvatarUrl ||
      row.posted_as_avatar_url ||
      row.authorAvatar ||
      row.author_avatar ||
      defaultAvatar,

    createdAt: row.createdAt || row.created_at || null,
    timeAgo: row.timeAgo || row.createdAt || row.created_at || '',

    body: row.content || row.body || '',

    reactionsSummary,
    reactionsCount: Number(reactionsSummary.total) || 0,

    commentsCount: row.commentsCount ?? row.comments_count ?? 0,
    sendCount: row.sendCount ?? 0,

    reactionNamesByType,
    reactionNames: reactionNamesLegacy,

    hasMedia: attachments.length > 0 || !!coverMediaFileId || !!imageUrl,
    imageUrl,
    coverMediaFileId,
    poll: row.poll || row.pollData || row.postPoll || null,
    postType: row.postType || row.post_type || (row.poll ? 'poll' : 'text'),
    pollOptions: Array.isArray(row.pollOptions) ? row.pollOptions : null,

    attachments,

    currentUserName,
    currentUserAvatar,

    raw: row,

    isSaved: !!isSavedFromBackend,
    isPinned,
  };
}

/**
 * Build the post object from a single /search result item.
 *
 * 1) If backend included a full post row (item.post / item.postRow / etc),
 *    we normalize that and use it.
 * 2) If the item itself already looks like a post row (has id/body/attachments),
 *    normalize it directly.
 * 3) Fallback: build a minimal post using postId + snippet, but still wiring
 *    coverMediaFileId / reactions when present.
 */
function buildPostFromSearchItem(item) {
  if (!item) return null;

  // 1) Embedded full post row
  const embeddedPost =
    item.post || item.rawPost || item.postRow || null;

  if (embeddedPost) {
    const normalized = normalizePostRow(embeddedPost);
    if (normalized) return normalized;
  }

  // 2) Item itself looks like a post
  if (
    item.id &&
    (item.body ||
      item.content ||
      item.attachments ||
      item.coverMediaFileId ||
      item.cover_media_file_id)
  ) {
    const normalized = normalizePostRow(item);
    if (normalized) return normalized;
  }

  // 3) Fallback minimal snippet post using postId
  const postId = item.postId || item.post_id || item.id;
  if (!postId) return null;

  const currentUserName = props.postingAsName || 'You';
  const currentUserAvatar = props.postingAsAvatarUrl || defaultAvatar;

  const coverMediaFileId =
    item.coverMediaFileId || item.cover_media_file_id || null;
  const imageUrl = coverMediaFileId
    ? buildCoverImageUrl(coverMediaFileId)
    : null;

  const reactionsSummary =
    item.reactionsSummary ||
    item.reactions || {
      total: Number(item.reactionsCount || 0) || 0,
      like: 0,
      love: 0,
      care: 0,
      haha: 0,
      wow: 0,
      sad: 0,
      angry: 0,
    };

  const reactionNamesByType =
    item.reactionNamesByType || {
      like: [],
      love: [],
      care: [],
      haha: [],
      wow: [],
      sad: [],
      angry: [],
    };

  const isSavedFromBackend =
    item.isSaved ?? item.is_saved ?? item.saved ?? false;
  const isPinned =
    item.isPinned ?? item.is_pinned ?? item.pinned ?? false;

  return {
    id: postId,

    authorName:
      item.postedAsName ||
      item.posted_as_name ||
      item.authorName ||
      item.author_name ||
      currentUserName,
    authorAvatar:
      item.postedAsAvatarUrl ||
      item.posted_as_avatar_url ||
      item.authorAvatar ||
      item.author_avatar ||
      defaultAvatar,

    createdAt: item.createdAt || null,
    timeAgo: item.createdAt || '',

    // use snippet if present; fallback to body
    body: item.snippet || item.body || item.content || '',

    reactionsSummary,
    reactionsCount: Number(reactionsSummary.total) || 0,

    commentsCount: item.commentsCount ?? 0,
    sendCount: item.sendCount ?? 0,

    reactionNamesByType,
    reactionNames: item.reactionNames || [],

    hasMedia: !!imageUrl,
    imageUrl,
    coverMediaFileId,

    attachments: Array.isArray(item.attachments) ? item.attachments : [],

    currentUserName,
    currentUserAvatar,

    raw: item,

    isSaved: !!isSavedFromBackend,
    isPinned: !!isPinned,
  };
}

/**
 * Core search runner – called when parent sets searchTerm
 * or when we click "Load more".
 */
async function runSearchForTerm(term, { append = false } = {}) {
  const trimmed = (term || '').trim();
  if (!trimmed || !props.featureInstanceId) return;
  if (loading.value) return;

  loading.value = true;
  errorMessage.value = '';
  currentTerm.value = trimmed;

  if (!append) {
    posts.value = [];
    offset.value = 0;
    hasMore.value = false;
    total.value = 0;
  }

  try {
    const params = new URLSearchParams({
      featureInstanceId: String(props.featureInstanceId),
      q: trimmed,
      limit: String(limit.value),
      offset: String(offset.value),
    });

    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/search?${params.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );

    const data = await res.json().catch(() => null);

    if (!res.ok || !data || data.success === false) {
      console.error(
        '[NewsfeedCreatorSearchTab] search error:',
        res.status,
        data
      );
      errorMessage.value =
        (data && data.error) ||
        'There was a problem running your search.';
      return;
    }

    const rawResults = Array.isArray(data.results) ? data.results : [];

    const mappedPosts = rawResults
      .map((item) => buildPostFromSearchItem(item))
      .filter(Boolean);

    if (append) {
      posts.value = posts.value.concat(mappedPosts);
    } else {
      posts.value = mappedPosts;
    }

    const pageCount = mappedPosts.length;
    const backendOffset =
      typeof data.offset === 'number' ? data.offset : offset.value;

    total.value =
      typeof data.total === 'number' ? data.total : posts.value.length;
    offset.value = backendOffset + pageCount;
    hasMore.value = !!data.hasMore && pageCount > 0;
  } catch (err) {
    console.error('[NewsfeedCreatorSearchTab] network error:', err);
    errorMessage.value =
      'Network error while searching. Please try again.';
  } finally {
    loading.value = false;
  }
}

async function loadMore() {
  if (!hasMore.value || !currentTerm.value) return;
  await runSearchForTerm(currentTerm.value, { append: true });
}

// 🔁 React when parent changes searchTerm
watch(
  () => props.searchTerm,
  (newTerm) => {
    const trimmed = (newTerm || '').trim();

    if (!trimmed || !props.featureInstanceId) {
      // Reset if cleared
      currentTerm.value = trimmed;
      posts.value = [];
      total.value = 0;
      offset.value = 0;
      hasMore.value = false;
      errorMessage.value = '';
      return;
    }

    runSearchForTerm(trimmed, { append: false });
  },
  { immediate: false }
);

// Optional: clear results when feature instance changes
watch(
  () => props.featureInstanceId,
  () => {
    posts.value = [];
    currentTerm.value = '';
    total.value = 0;
    offset.value = 0;
    hasMore.value = false;
    errorMessage.value = '';
  }
);

// Expose a manual runner if parent wants to force refresh
defineExpose({
  runSearchForTerm,
});

/* ─────────────────────────────────────────────
 * Save / Unsave / Pin handlers (local state)
 * ───────────────────────────────────────────*/

function handleSavePostFromSearch({ postId }) {
  if (!postId) return;

  posts.value = posts.value.map((p) =>
    String(p.id) === String(postId) ? { ...p, isSaved: true } : p
  );
}

async function handleUnsavePostFromSearch({ postId }) {
  if (!postId) return;

  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(
        postId
      )}/save`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error(
        '[NewsfeedCreatorSearchTab] unsave backend NOT OK:',
        res.status,
        text
      );
      return;
    }

    await res.json().catch(() => ({}));

    posts.value = posts.value.map((p) =>
      String(p.id) === String(postId) ? { ...p, isSaved: false } : p
    );
  } catch (err) {
    console.error(
      '[NewsfeedCreatorSearchTab] error unsaving post:',
      err
    );
  }
}

function handlePinChanged({ postId, isPinned }) {
  if (!postId) return;

  const updated = posts.value.map((p) =>
    String(p.id) === String(postId) ? { ...p, isPinned: !!isPinned } : p
  );

  // Optional: sort pinned posts first, then newest
  updated.sort((a, b) => {
    if (a.isPinned !== b.isPinned) {
      return a.isPinned ? -1 : 1;
    }
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return bTime - aTime;
  });

  posts.value = updated;
}
</script>



